import { Fragment, useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux";
import logo from '../../logo.png';
import { useLocation } from "react-router-dom";
import { Collapse } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { signOut } from "../../services/baseService";
import { setSearch } from "../../slices/commonReducer";
import { FaClipboardList, FaFileInvoiceDollar } from 'react-icons/fa';
import useCheckAuthorization from "../../hooks/checkAuthorization";
import { hasPermission } from '../../utils/permissions';
import { PAGE_PERMISSIONS } from "../../constants/user";
import {
    FaBars,
    FaTrello,
    FaHdd,
    FaUser,
    FaClock,
    FaUserTie,
    FaFileAlt,
    FaSearch,
    FaExchangeAlt,
    FaArrowLeft,
    FaUsersCog,
    FaUsers,
    FaUserShield,
    FaChevronDown,
    FaChevronUp,
    FaShieldAlt,
    FaBuilding
} from 'react-icons/fa';
import { GrUserWorker } from "react-icons/gr";
import useSidebar from "../../hooks/useSidebar";

const sidebarMenu = [
    {
        label: "Dashboard",
        icon: <FaTrello className="icon nav-icon" />,
        link: "/",
        activeKeys: ["/", "/dashboard"] // Add multiple possible active routes
    },
    {
        label: "User Management",
        icon: <FaUsersCog className="icon nav-icon" />,
        activeKeys: ["/user-management", "/manage-roles", "/view-roles"], // Parent active when any child is active
        children: [
            {
                label: "Users List",
                icon: <FaUsers className="icon nav-icon me-2" />,
                link: "/user-management",
                activeKeys: ["/user-management", "/employee"],
                permissions: PAGE_PERMISSIONS.user,
            },
            {
                label: "Manage Hierarchy",
                icon: <FaShieldAlt className="icon nav-icon me-2" />,
                link: "/manage-roles",
                activeKeys: ["/manage-roles"],
                permissions: PAGE_PERMISSIONS.rbac,
            },
            {
                label: "Roles & Permissions",
                icon: <FaUserShield className="icon nav-icon me-2" />,
                link: "/view-roles",
                activeKeys: ["/view-roles"],
                permissions: PAGE_PERMISSIONS.rbac,
            }
        ],
        permissions: PAGE_PERMISSIONS.user_management,
    },
    {
        label: "Client Management",
        icon: <FaUsersCog className="icon nav-icon" />,
        activeKeys: ["/client-management"],
        children: [
            {
                label: "Clients List",
                icon: <FaUserTie className="icon nav-icon me-2" />,
                link: "/client-management",
                activeKeys: ["/client-management", "/client-process"]
            },
        ],

        permissions: PAGE_PERMISSIONS.client,
    },
    {
        label: "Departments",
        icon: <FaBuilding className="icon nav-icon" />,
        link: "/manage-department",
        activeKeys: ["/manage-department"],
        permissions: PAGE_PERMISSIONS.department,
    },
    {
        label: "Facilitation",
        icon: <FaExchangeAlt className="icon nav-icon" />,
        link: "/Export-Import-Facilitation",
        activeKeys: ["/Export-Import-Facilitation"],
        permissions: PAGE_PERMISSIONS.facilitation,
    },
    {
        label: "Activity Log",
        icon: <FaClipboardList className="icon nav-icon" />,
        link: "/activity-log",
        activeKeys: ["/activity-log"]
    },
    {
        label: "HSN/SAC",
        icon: <FaFileAlt className="icon nav-icon" />,
        link: "/hsn/sac",
        activeKeys: ["/hsn/sac", "/hsn", "/sac", "/query-details"], // Multiple possible routes
        permissions: PAGE_PERMISSIONS.hsn,
    },
    {
        label: "Manage Profile",
        icon: <FaHdd className="icon nav-icon" />,
        link: "/manage-profile",
        activeKeys: ["/manage-profile"]
    },
    {
        label: "Search",
        icon: <FaSearch className="icon nav-icon" />,
        action: "enableSearch",
        activeKeys: ["/search"]
    },
];

function Sidebar() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { permissions } = useSelector(state => state.common);
    const { data } = useSelector((state) => state.clients)

    const handleSignOut = () => {
        signOut().then(response => {
            navigate('/login');
        })
    }
    const { isSidebarCollapsed, toggle } = useSidebar();
    const [openMenus, setOpenMenus] = useState({});

    const toggleMenu = (label) => {
        setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
    };

    const location = useLocation();

    // Helper function to check if a menu item is active
    const isMenuActive = (activeKeys, currentPath) => {
        if (!activeKeys) return false;
        return activeKeys.some(key => {
            // Exact match
            if (key === currentPath) return true;
            // Starts with match (for nested routes)
            if (currentPath.startsWith(key + '/')) return true;
            return false;
        });
    };

    useEffect(() => {
        sidebarMenu.forEach(item => {
            if (item.children) {
                const isChildActive = item.children.some(child =>
                    isMenuActive(child.activeKeys, location.pathname)
                );
                if (isChildActive) {
                    setOpenMenus(prev => ({ ...prev, [item.label]: true }));
                }
            }
        });
    }, [location.pathname]);

    return (
        <div className="vertical-menu">
            {/* LOGO */}
            <div className="navbar-brand-box">
                <Link to="/" className="logo logo-dark">
                    <span className="logo-sm">
                        <img src="/assets/images/ziptax-sm.png" alt="Logo Small" height="44" className="h-8 inline" />
                    </span>
                    <span className="logo-lg">
                        <img src="/assets/images/ziptax.png" alt="Logo Dark" height="44" className="h-11 inline" />
                    </span>
                </Link>
                <Link to="/" className="logo logo-light">
                    <span className="logo-sm">
                        <img src="/assets/images/ziptax-sm.png" alt="Logo Light Small" height="44" className="h-8 inline max-w-max" />
                    </span>
                    <span className="logo-lg">
                        <img src="/assets/images/ziptax.png" alt="Logo Light" height="44" className="h-11 inline" />
                    </span>
                    <span className="logo-lg-brand">
                        <img src="/assets/images/ziptax.png" alt="Logo Brand Light" height="44" className="h-11 inline" />
                    </span>
                </Link>
            </div>

            <button
                type="button"
                className="btn btn-sm px-3 font-size-16 header-item vertical-menu-btn"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggle();
                }}
            >
                <FaBars />
            </button>

            <div className={`sidebar-menu-scroll flex-1 flex flex-col ${isSidebarCollapsed ? '' : 'overflow-y-auto max-h-[calc(100vh-64px)]'}`}>
                {/* Sidemenu */}
                <div id="sidebar-menu" className="flex-1">
                    <ul className="metismenu list-unstyled" id="side-menu">
                        {sidebarMenu.map((item, index) => {
                            const itemHasPermission = !item.permissions || hasPermission(permissions, item.permissions);
                            const isActive = isMenuActive(item.activeKeys, location.pathname);

                            if (!itemHasPermission) return null;

                            if (!item.children) {
                                return (
                                    <li key={index}>
                                        {item.action === "enableSearch" ? (
                                            <Link
                                                to="/search"
                                                className={isActive ? 'active-menu-item' : ''}
                                            >
                                                {item.icon}
                                                <span className="menu-item">{item.label}</span>
                                            </Link>
                                        ) : (
                                            <Link
                                                to={item.link}
                                                className={isActive ? 'active-menu-item' : ''}
                                            >
                                                {item.icon}
                                                <span className="menu-item">{item.label}</span>
                                            </Link>
                                        )}
                                    </li>
                                );
                            }

                            // Filter child items based on permissions
                            const childItems = item.children.filter(
                                (child) => !child.permissions || hasPermission(permissions, child.permissions)
                            );

                            if (childItems.length === 0) return null;

                            return (
                                <li key={index}>
                                    <div
                                        onClick={() => toggleMenu(item.label)}
                                        aria-controls={`${item.label}-collapse`}
                                        aria-expanded={!!openMenus[item.label]}
                                        className={`d-flex align-items-center cursor-pointer menu-parent-link ${isActive ? 'active-menu-item' : ''}`}
                                    >
                                        <div className="d-flex align-items-center">
                                            {item.icon}
                                            <span className="menu-item ms-2">{item.label}</span>
                                        </div>
                                        <div className="ms-auto">
                                            {openMenus[item.label] ? <FaChevronUp /> : <FaChevronDown />}
                                        </div>
                                    </div>
                                    <Collapse in={openMenus[item.label]}>
                                        <ul className="list-unstyled ms-4" id={`${item.label}-collapse`}>
                                            {childItems.map((child, childIndex) => {
                                                const isChildActive = isMenuActive(child.activeKeys, location.pathname);
                                                return (
                                                    <li key={childIndex}>
                                                        <Link
                                                            to={child.link}
                                                            className={`d-flex align-items-center ${isChildActive ? 'active-menu-item' : ''}`}
                                                        // style={{ backgroundColor: '#E6F2FF', }}
                                                        >
                                                            {child.icon}
                                                            <span className="m-1 p-0" >{child.label}</span>
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </Collapse>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Sign Out */}
                <div
                    className="cursor-pointer p-3  text-muted text-base  hover:bg-opacity-100 transition-colors duration-200 ease-in-out sticky bottom-0 bg-[#f8f9fa] z-10 flex items-center justify-center"
                    onClick={handleSignOut}
                    title="Sign Out"
                >
                    <FaArrowLeft className="icon nav-icon mr-2" />
                    {!isSidebarCollapsed && <span>Sign Out</span>}
                </div>
            </div>
        </div>
    );
}

export default Sidebar;